import { useMutation, useQuery } from 'react-query'
import axios, { Method } from 'axios'
import { toast } from 'react-toastify'

/**
 * Splits and parses the JWT session token and returns the contents
 */
export const useSessionDetails = (sessionToken: string) => {
  if (!sessionToken) {
    return {}
  }
  const [, payload] = sessionToken.split('.')
  const decodedPayload = JSON.parse(atob(payload))
  return decodedPayload
}

/**
 * Calls the API endpoint for a specific url
 */
export const useApiCall = <T>(sessionToken: string, url: string) => {
  return useQuery<T>([url], {
    enabled: !!sessionToken,
    queryFn: async () => {
      const response = await fetch(url, {
        headers: { 'X-Session-Token': sessionToken },
      })
      return response.json() as T
    },
  })
}

/**
 * Calls the /api/ endpoint for a specicif url
 */
export const useClientApiCall = <T>(sessionToken: string, path: string) => {
  const { clientId } = useSessionDetails(sessionToken)
  path = path.startsWith('/') ? path.replace('/', '') : path
  const url = `${APPS_API_DOMAIN_NAME}/clients/${clientId}/api/${path}`

  return useApiCall<T>(sessionToken, url)
}

export const useClientApiMutation = <T>(
  sessionToken: string,
  path: string,
  method: Method = 'POST'
) => {
  const { clientId } = useSessionDetails(sessionToken)
  path = path.startsWith('/') ? path.replace('/', '') : path
  const url = `${APPS_API_DOMAIN_NAME}/clients/${clientId}/api/${path}`

  return useMutation(async (data: T | undefined) => {
    const response = await axios({
      method,
      url,
      headers: {
        'Content-Type': 'application/json',
        'X-Session-Token': sessionToken,
      },
      data: data,
      validateStatus: (status) =>
        (status >= 200 && status < 300) || status === 404,
    })

    if (response.status === 404) {
      toast.error((response.data as any)?.error)
    }

    const result = response.data as any
    toast.success('connected successfully')
    if (result?.redirectTo) {
      console.log('Redirecting to:', result.redirectTo)
      window.location.href = result.redirectTo
      return
    }

    return response.data as T
  })
}
